import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { createRefreshCallback } from "../../auth/refreshCallback";

/**
 * Response from like status endpoint
 */
interface LikeStatusResponse {
  liked: boolean;
  totalLikes: number;
}

/**
 * Response from toggle like endpoint
 */
interface ToggleLikeResponse {
  liked: boolean;
}

/**
 * Hook to manage title like state with optimistic updates
 *
 * @param titleId - The ID of the title to manage likes for
 * @returns Object with like state and mutation functions
 *
 * @example
 * const { liked, totalLikes, toggle, loading, error } = useTitleLike('title-123');
 *
 * // Toggle like with optimistic update
 * await toggle();
 */
export function useTitleLike(titleId: string) {
  const { accessToken, refreshAuth } = useAuth();
  const [liked, setLiked] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Fetch initial like status
  const fetchLikeStatus = useCallback(async () => {
    if (!accessToken || !titleId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE}/v1/titles/${titleId}/like-status`;
      const response = await authenticatedFetch<LikeStatusResponse>(
        url,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setLiked(response.liked);
      setTotalLikes(response.totalLikes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch like status");
    } finally {
      setLoading(false);
    }
  }, [accessToken, titleId, API_BASE, refreshAuth]);

  // Toggle like with optimistic update
  const toggle = useCallback(async () => {
    if (!accessToken || mutating) return;

    // Optimistic update
    const previousLiked = liked;
    const previousTotal = totalLikes;
    setLiked(!liked);
    setTotalLikes(liked ? totalLikes - 1 : totalLikes + 1);
    setMutating(true);
    setError(null);

    try {
      const url = `${API_BASE}/v1/titles/${titleId}/like`;
      const response = await authenticatedFetch<ToggleLikeResponse>(
        url,
        { method: "POST" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      // Update with server response
      setLiked(response.liked);

      // Refetch to get accurate total count
      await fetchLikeStatus();
    } catch (err) {
      // Rollback on error
      setLiked(previousLiked);
      setTotalLikes(previousTotal);
      setError(err instanceof Error ? err.message : "Failed to toggle like");
    } finally {
      setMutating(false);
    }
  }, [accessToken, liked, totalLikes, mutating, titleId, API_BASE, refreshAuth, fetchLikeStatus]);

  // Load initial status
  useEffect(() => {
    void fetchLikeStatus();
  }, [fetchLikeStatus]);

  return {
    liked,
    totalLikes,
    toggle,
    loading,
    mutating,
    error,
    refetch: fetchLikeStatus,
  };
}
